import { ApplicationConfig, inject, PLATFORM_ID, provideAppInitializer, provideZonelessChangeDetection } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { provideClientHydration, withIncrementalHydration } from "@angular/platform-browser";
import { routes } from "./app.routes";
import { provideRouter, withInMemoryScrolling } from "@angular/router";
import { environment } from "src/environments/environment";
import { provideHttpClient, withFetch } from "@angular/common/http";
import { provideNgxMask } from "ngx-mask";

import { initializeApp, getApp, getApps } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { initializeAuth, getAuth, indexedDBLocalPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getRemoteConfig } from 'firebase/remote-config';
import { FirebaseApp } from 'firebase/app';

import { FIREBASE_APP, FIRESTORE, FUNCTIONS, AUTH, ANALYTICS, APP_CHECK, REMOTE_CONFIG } from './shared/utils/firebase-tokens';
import { AnalyticsService } from './shared/analytics.service';
import { initMainPageData } from "./shared/utils/main-page-initializer";

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideClientHydration(withIncrementalHydration()),
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled',
            }),
        ),

        {
            provide: FIREBASE_APP,
            useFactory: () => getApps().length ? getApp() : initializeApp(environment.firebase)
        },
        {
            provide: FIRESTORE,
            useFactory: (app: FirebaseApp) => getFirestore(app),
            deps: [FIREBASE_APP]
        },
        {
            provide: FUNCTIONS,
            useFactory: (app: FirebaseApp) => getFunctions(app),
            deps: [FIREBASE_APP]
        },
        {
            provide: AUTH,
            useFactory: (app: FirebaseApp, platformId: object) =>
                isPlatformBrowser(platformId)
                    ? initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] })
                    : getAuth(app),
            deps: [FIREBASE_APP, PLATFORM_ID]
        },
        // ANALYTICS is kept for type compatibility but is no longer the live
        // handle. AnalyticsService owns Firebase Analytics now and only
        // initializes once ConsentService.accepted() flips true (LGPD gate).
        {
            provide: ANALYTICS,
            useValue: null
        },
        {
            provide: APP_CHECK,
            useFactory: (app: FirebaseApp, platformId: object) => {
                if (!isPlatformBrowser(platformId)) return null;
                if (!environment.production) {
                    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string })
                        .FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheckDebugToken;
                }
                return initializeAppCheck(app, {
                    provider: new ReCaptchaEnterpriseProvider(environment.appCheckRecaptchaSiteKey),
                    isTokenAutoRefreshEnabled: true,
                });
            },
            deps: [FIREBASE_APP, PLATFORM_ID]
        },
        {
            provide: REMOTE_CONFIG,
            useFactory: (app: FirebaseApp, platformId: object) => {
                if (!isPlatformBrowser(platformId)) return null;
                const rc = getRemoteConfig(app);
                rc.settings.minimumFetchIntervalMillis = environment.production
                    ? 12 * 60 * 60 * 1000
                    : 1  * 60 * 60 * 1000;
                rc.settings.fetchTimeoutMillis = 60_000;
                rc.defaultConfig = {
                    ai_search_enabled: false,
                    ai_similar_properties_enabled: false,
                };
                return rc;
            },
            deps: [FIREBASE_APP, PLATFORM_ID]
        },

        provideHttpClient(withFetch()),
        provideNgxMask(),

        provideAppInitializer(() => { inject(APP_CHECK); }),
        // Eagerly construct AnalyticsService so its consent-watching effect
        // is registered before any component injects it. The effect stays
        // dormant until ConsentService.accepted() flips true.
        provideAppInitializer(() => { inject(AnalyticsService); }),
        provideAppInitializer(() => initMainPageData()())
    ]
};
